<script setup lang="ts">
import { RouterLink } from "vue-router";
import { ref, onMounted, watch, computed } from "vue";
import type { Ref } from "vue";
import type { PriceData, StockData, HoldingData } from "@/interfaces/interfaces";
import { formatPercent, formatNumber } from "@/helpers/formatters";
import { APIURL } from "@/defaults";

const props = defineProps<{
    data: StockData;
    priceData: PriceData | null;
    holdingData: HoldingData | null;
}>();

const percentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
};

const change = computed(() => {
    if (props.priceData?.previous_close) {
        return props.priceData.close / props.priceData.previous_close - 1;
    }
    return 0;
});
</script>

<template>
    <RouterLink :to="`/isin/${data.isin}`" class="sidebar-link stock-link">
        <img :src="`${APIURL}/stocks/${data.isin}/icon`" alt="" class="stock-link-icon" loading="lazy" />
        <span class="stock-link-name">{{data.name}}</span>
        <span class="stock-link-isin" :class="['stock-link-isin', {alert: holdingData ? Math.abs(holdingData.booked_data.fund_proportion - holdingData.target_weight) > 0.005 : false}]">
            <template v-if="holdingData">
                {{formatPercent(holdingData.booked_data.fund_proportion, 2)}} / {{formatPercent(holdingData.target_weight, 2)}}
            </template>
        </span>
        <div class="stock-link-price" v-if="priceData">
            <div class="stock-link-price-current">{{formatNumber(priceData.close, 2)}}</div>
            <div class="stock-link-price-change" :class="{'--negative': change < 0, '--positive': change > 0}">{{formatPercent(change, 2, true)}}</div>
        </div>

    </RouterLink>
</template>

<style>
.sidebar-link {
    padding: 12px 20px;
    color: #000;
    display: flex;
    justify-content: flex-start;
    flex-flow: row;
    gap: 12px;
}
/*
@media (max-height: 800px) {
    .sidebar-link--nav {
        padding: 6px 20px;
    }
}
@media (min-height: 800px) and (max-height: 1200px) {
    .sidebar-link--nav {
        padding: calc(6px + (12 - 6) * ((100vh - 800px) / (1200 - 800))) 20px;
    }
}
*/
.sidebar-link.router-link-exact-active,
a.sidebar-link.router-link-exact-active:hover,
a.sidebar-link:hover {
    color: #000;
    background: rgb(247, 229, 218);

}
.sidebar-link.router-link-exact-active {
    box-shadow: 4px 0 0 rgb(226, 159, 118) inset;
}

.stock-link {
    display: grid;
    grid-template-columns: 48px 1fr auto;
    grid-template-areas: "icon name price"
                         "icon isin price";
    grid-column-gap: 8px;
    grid-row-gap: 0;
}
.fund-link {
    grid-template-columns: 1fr auto;
    grid-template-areas: "name price"
                         "isin price";
    gap: 0;
}
.stock-link-icon {
    grid-area: icon;
    width: 48px;
    height: 48px;
    -webkit-filter: saturate(0) brightness(0.75) contrast(2);
    filter: saturate(0) brightness(0.75) contrast(2);
}
.cash-link .stock-link-icon {
    height: 32px;
}
.cash-link .stock-link-price-current {
    font-size: 14px;
    line-height: 1.2;
}
.stock-link-name {
    font-weight: 600;
    grid-area: name;
}
.stock-link-isin {
    grid-area: isin;
    font-size: 12px;
    opacity: 0.6;
}
.stock-link-isin.alert {
    color: #FF910D;
    font-weight: 800;
    opacity: 1;
}
.stock-link-price {
    display: flex;
    flex-flow: column;
    align-items: stretch;
    font-variant-numeric: tabular-nums lining-nums;
    grid-area: price;
}
.stock-link-price-current {
    font-weight: 600;
    text-align: right;
    min-width: 72px;
}
.stock-link-price-change {
    background: rgba(0,0,0,0.2);
    border-radius: 3px;
    padding: 4px 6px;
    line-height: 1;
    font-size: 12px;
    text-align: right;
    font-weight: 600;
    color: #fff;
}
.stock-link-price-change.--positive {
    background: var(--teq-green);
}
.stock-link-price-change.--negative {
    background: var(--teq-red);
}
</style>
